import { Form, Input } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useUpdateScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestUpdateModalId } from '../../constants/constants';
import { selectTempScanRequestId, setTempScanRequestId } from '../../store/scan-requests-slice';

interface PropsContent {
  formId: string;
  inputsData: Record<
    string,
    {
      label: string;
      placeholder: string;
      initialValue: string;
    }
  >;
}

export type PreviewUpdateFormType = {
  [key: string]: string;
};

const ScanRequestFormModalContent = ({
  inputsData,
  formId,
  isOpen,
  afterClose,
}: PropsContent & LazyModalComponentProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['scan-requests', 'common']);
  const [updateScanRequest, { isLoading }] = useUpdateScanRequestMutation();
  const [form] = Form.useForm<PreviewUpdateFormType>();
  const activeScanRequestId = useAppSelector(selectTempScanRequestId);

  const closePreviewModal = () => {
    dispatch(closeModal(scanRequestUpdateModalId));
    dispatch(setTempScanRequestId(null));
  };

  const initialValues: PreviewUpdateFormType = useMemo(() => {
    return Object.keys(inputsData)
      .map(key => ({
        [key]: inputsData[key].initialValue,
      }))
      .reduce((accumulator, object) => ({ ...accumulator, ...object }), {});
  }, [inputsData]);

  const submitPreviewUpdateForm = (formValues: PreviewUpdateFormType) => {
    if (!activeScanRequestId) {
      return;
    }
    updateScanRequest({
      name: formValues.name,
      id: activeScanRequestId,
    })
      .unwrap()
      .then(() => {
        closePreviewModal();
        dispatch(
          setAlert({
            id: scanRequestAlertId,
            type: 'success',
            title: t('scanRequestUpdateFormModal.message.scanRequestUpdated'),
          }),
        );
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          error.data?.errors || { [Object.keys(inputsData)[0]]: [t('errors.unexpected', { ns: 'common' })] },
        );
      });
  };

  return (
    <Modal
      title={t('scanRequestUpdateFormModal.title')}
      onCancel={() => !isLoading && closePreviewModal()}
      isLoading={isLoading}
      isOpen={isOpen}
      afterClose={afterClose}
      formId={formId}
    >
      <Form id={formId} form={form} initialValues={initialValues} layout="vertical" onFinish={submitPreviewUpdateForm}>
        {Object.entries(inputsData).map(inputValue => {
          return (
            <Form.Item key={inputValue[0]} name={inputValue[0]} label={inputValue[1].label}>
              <Input placeholder={inputValue[1].placeholder} />
            </Form.Item>
          );
        })}
      </Form>
    </Modal>
  );
};

const ScanRequestUpdateFormModal = (props: PropsContent) => {
  return (
    <LazyModal
      modalId={scanRequestUpdateModalId}
      Component={componentProps => <ScanRequestFormModalContent {...props} {...componentProps} />}
    />
  );
};

export default ScanRequestUpdateFormModal;
